import React, { FC, useMemo } from 'react';
import TextEditor from 'app/modules/textEditor';
import { PageNumberType, TYPE } from '@kontentino/kontentino-constants/Pages';
import AudienceRestrictionsModal from 'app/modules/posts/modules/detail/modules/audienceRestrictions/AudienceRestrictionsModal';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import { useToast } from 'app/hooks/useToast';
import { Chip, Icon, mergeTailwindClasses } from '@kontentino/ui';
import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';
import { OptionsBarConfig } from 'app/modules/textEditor/components/OptionsBar';
import { getPostTextMaxCharLimit } from 'constants/post';
import { UseSetStateAction } from 'utils/hooks/useSetState';
import useBoolean from 'utils/hooks/useBoolean';
import { usePostTextCharacterCounts } from 'app/modules/posts/hooks/usePostTextCharacterCounts';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import {
  countActiveAudienceRestrictions,
  createCompleteAudienceRestrictions,
} from 'app/modules/posts/modules/detail/modules/audienceRestrictions/utils/audienceRestrictions';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

type Props = {
  value: Pick<PostComposerFormState, 'text' | 'page' | 'audienceRestrictions'>;
  onChange: UseSetStateAction<PostComposerFormState>;
  classNames?: React.ComponentProps<typeof TextEditor>['classNames'];
} & Pick<React.ComponentProps<typeof TextEditor>, 'onInit' | 'disabled'>;

const PostComposerContentTextEditor: FC<Props> = ({
  value,
  onChange,
  disabled,
  onInit,
  classNames,
}) => {
  const { t } = useTranslation();
  const { accountPlanType } = useSubscriptionInfo();
  const [isShown, setIsShown] = useBoolean(false);
  const characterCounts = usePostTextCharacterCounts({
    text: value.text,
    pageType: value.page.type,
  });
  const toast = useToast();

  const hasAudienceRestrictions = (
    [TYPE.LINKEDIN, TYPE.FACEBOOK] as PageNumberType[]
  ).includes(value.page.type as PageNumberType);

  const activeAudienceRestrictionsCount = useMemo(() => {
    return countActiveAudienceRestrictions(value.audienceRestrictions);
  }, [value.audienceRestrictions]);

  function getOptionsBarConfig(): OptionsBarConfig {
    return {
      charactersLimit: getPostTextMaxCharLimit(value.page),
      count: characterCounts,
      additionalButtons: hasAudienceRestrictions
        ? [
            {
              variant: 'plain',
              children: (
                <div className="tw-relative">
                  <Icon icon={faUsers} size={14} />
                  {activeAudienceRestrictionsCount > 0 && (
                    <Chip
                      variant="alert"
                      className="tw-absolute -tw-right-4 tw-top-0"
                    >
                      {activeAudienceRestrictionsCount}
                    </Chip>
                  )}
                </div>
              ),
              name: t('audienceRestrictions.title'),
              dataName: 'audience-restrictions',
              onClick: setIsShown.toggle,
            },
          ]
        : [],
    };
  }

  return (
    <>
      <TextEditor
        accountPlanType={accountPlanType}
        dataPrefix="post-form"
        optionsBarConfig={getOptionsBarConfig()}
        initialValue={value.text}
        onDebouncedChange={(text) => onChange({ text })}
        page={value.page}
        disabled={disabled}
        onInit={onInit}
        classNames={{
          ...classNames,
          editor: mergeTailwindClasses(
            clsx('tw-min-h-[4.25rem]', classNames?.editor),
          ),
          refineTextSelection: 'tw-m-2',
        }}
      />
      {hasAudienceRestrictions && (
        <AudienceRestrictionsModal
          isShown={isShown}
          onClose={setIsShown.toggle}
          page={{ id: value.page.id, type: value.page.type }}
          initialValue={value.audienceRestrictions}
          onSubmit={(audienceRestrictions) => {
            const completeAudienceRestrictions =
              createCompleteAudienceRestrictions(audienceRestrictions);
            onChange({ audienceRestrictions: completeAudienceRestrictions });
            toast(t('audienceRestrictions.savedToastMessage'));
            setIsShown.toggle();
          }}
        />
      )}
    </>
  );
};

export default PostComposerContentTextEditor;
